import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { isNational } from "@/lib/roles";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/context/I18nContext";
import { FileText, Plus, Download, Archive, ArchiveRestore, Trash2, Eye } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { PdfFrame } from "@/components/PdfFrame";

const CATEGORIES = ["Manuals", "Activity ideas", "Progress badge materials", "Forms", "Policies", "Camp documents", "Training materials", "Leader resources"];

export default function Resources() {
  const { user } = useAuth();
  const { t } = useT();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", category: "Manuals", description: "", file_data: "", file_name: "", file_type: "" });
  const [category, setCategory] = useState("all");
  const [showArchived, setShowArchived] = useState(false);

  const load = () => {
    const params = new URLSearchParams();
    if (category !== "all") params.set("category", category);
    if (showArchived) params.set("include_archived", "true");
    api.get(`/resources?${params}`).then(r => setItems(r.data));
  };
  useEffect(() => { load(); }, [category, showArchived]);

  const canUpload = user?.role && user.role !== "scout";

  const onFile = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const reader = new FileReader();
    reader.onload = () => setForm({ ...form, file_data: reader.result, file_name: f.name, file_type: f.type });
    reader.readAsDataURL(f);
  };
  const save = async () => {
    try { await api.post("/resources", form); toast.success("Uploaded"); setOpen(false); load(); }
    catch { toast.error("Failed"); }
  };
  const archive = async (rid, archived) => {
    try {
      await api.post(`/resources/${rid}/${archived ? "unarchive" : "archive"}`);
      toast.success(archived ? "Restored" : "Archived");
      load();
    } catch { toast.error("Failed"); }
  };
  const remove = async (rid) => {
    if (!window.confirm("Delete this resource?")) return;
    try { await api.delete(`/resources/${rid}`); toast.success("Deleted"); load(); }
    catch { toast.error("Failed"); }
  };
  const download = async (rid, name) => {
    try {
      const { data } = await api.get(`/resources/${rid}`);
      if (!data.file_data) return toast.info("No file attached");
      const a = document.createElement("a"); a.href = data.file_data; a.download = name || "resource"; a.click();
    } catch { toast.error("Failed"); }
  };

  const [preview, setPreview] = useState(null); // { title, file_name, file_type, file_data }
  const openPreview = async (r) => {
    try {
      const { data } = await api.get(`/resources/${r.resource_id}`);
      if (!data.file_data) { toast.info("No file attached"); return; }
      setPreview({ ...r, file_data: data.file_data });
    } catch { toast.error("Could not load preview"); }
  };

  const detectKind = (p) => {
    const t = (p?.file_type || "").toLowerCase();
    const n = (p?.file_name || "").toLowerCase();
    if (t.startsWith("image/") || /\.(png|jpe?g|gif|webp|bmp|svg|heic|heif)$/i.test(n)) return "image";
    if (t === "application/pdf" || n.endsWith(".pdf")) return "pdf";
    if (t.startsWith("video/") || /\.(mp4|mov|webm|m4v)$/i.test(n)) return "video";
    if (t.startsWith("audio/") || /\.(mp3|wav|ogg|m4a)$/i.test(n)) return "audio";
    if (t.startsWith("text/") || /\.(txt|md|csv|json|log|xml|html?)$/i.test(n)) return "text";
    return "unknown";
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="uppercase-label">{t("Library", "Գրադարան")}</div>
          <h1 className="font-display text-4xl lg:text-5xl font-black tracking-tight mt-1">{t("Resources", "Ռեսուրսներ")}</h1>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          {canUpload && (
            <label className="flex items-center gap-2 text-xs uppercase tracking-widest font-bold cursor-pointer">
              <Switch checked={showArchived} onCheckedChange={setShowArchived} data-testid="res-show-archived"/>
              Archived
            </label>
          )}
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="w-56"><SelectValue placeholder="Category"/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          {canUpload && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button className="btn-pill bg-[hsl(12,65%,63%)]" data-testid="new-res-btn"><Plus size={16} className="mr-2"/>{t("Upload", "Վերբեռնել")}</Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader><DialogTitle>{t("Upload Resource", "Վերբեռնել ռեսուրս")}</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div><Label>Title</Label><Input value={form.title} onChange={e => setForm({...form, title: e.target.value})}/></div>
                  <div><Label>Category</Label>
                    <Select value={form.category} onValueChange={v => setForm({...form, category: v})}>
                      <SelectTrigger><SelectValue/></SelectTrigger>
                      <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>Description</Label><Textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})}/></div>
                  <div><Label>File</Label><Input type="file" onChange={onFile}/></div>
                  <Button onClick={save} className="btn-pill w-full bg-[hsl(149,40%,30%)]">Upload</Button>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map(r => (
          <Card key={r.resource_id} className={`clay-card p-6 hover-lift relative ${r.archived ? "opacity-60" : ""}`} data-testid={`res-${r.resource_id}`}>
            {canUpload && (
              <div className="absolute top-3 right-3 flex gap-1">
                <button
                  onClick={() => archive(r.resource_id, r.archived)}
                  className="w-8 h-8 rounded-full text-muted-foreground hover:bg-[hsl(32,87%,67%)]/20 hover:text-[hsl(32,87%,55%)] flex items-center justify-center"
                  data-testid={`archive-res-${r.resource_id}`}
                  title={r.archived ? "Unarchive" : "Archive"}
                >
                  {r.archived ? <ArchiveRestore size={14}/> : <Archive size={14}/>}
                </button>
                {isNational(user) && (
                  <button
                    onClick={() => remove(r.resource_id)}
                    className="w-8 h-8 rounded-full text-muted-foreground hover:bg-[hsl(0,65%,55%)]/10 hover:text-[hsl(0,65%,55%)] flex items-center justify-center"
                    data-testid={`del-res-${r.resource_id}`}
                    title="Delete"
                  ><Trash2 size={14}/></button>
                )}
              </div>
            )}
            <div className="flex items-start gap-3">
              <div className="w-11 h-11 rounded-2xl bg-[hsl(149,40%,30%)]/15 text-[hsl(149,40%,30%)] flex items-center justify-center">
                <FileText size={20}/>
              </div>
              <div className="flex-1">
                <div className="uppercase-label">{r.category}</div>
                <div className="font-semibold mt-1">{r.title}</div>
                <div className="text-xs text-muted-foreground mt-1 line-clamp-2">{r.description}</div>
                <Button size="sm" variant="ghost" className="mt-3" onClick={() => openPreview(r)} data-testid={`preview-res-${r.resource_id}`}><Eye size={14} className="mr-1"/>Preview</Button>
                <Button size="sm" variant="ghost" className="mt-3 ml-1" onClick={() => download(r.resource_id, r.file_name)} data-testid={`download-res-${r.resource_id}`}><Download size={14} className="mr-1"/>Download</Button>
              </div>
            </div>
          </Card>
        ))}
      </div>

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-5xl max-h-[95vh] p-0 overflow-hidden flex flex-col" data-testid="resource-preview-dialog">
          <DialogHeader className="px-6 pt-6 pb-3 flex-shrink-0">
            <DialogTitle className="flex items-center gap-3 flex-wrap">
              <span className="truncate">{preview?.title || preview?.file_name}</span>
              {preview?.file_name && <span className="text-xs text-muted-foreground font-normal">{preview.file_name}</span>}
              <Button size="sm" variant="outline" className="ml-auto btn-pill" onClick={() => preview && download(preview.resource_id, preview.file_name)} data-testid="preview-download">
                <Download size={14} className="mr-1"/>Download
              </Button>
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-auto bg-muted/30 p-4">
            {preview && (() => {
              const kind = detectKind(preview);
              if (kind === "image") return <img src={preview.file_data} alt={preview.title} className="max-h-full mx-auto rounded-lg shadow"/>;
              if (kind === "pdf")   return <PdfFrame title={preview.title} data={preview.file_data} className="w-full h-[75vh] rounded-lg bg-white" />;
              if (kind === "video") return <video src={preview.file_data} controls className="w-full max-h-[75vh] rounded-lg bg-black"/>;
              if (kind === "audio") return <audio src={preview.file_data} controls className="w-full"/>;
              if (kind === "text") {
                try {
                  const b64 = preview.file_data.split(",")[1] || "";
                  const txt = decodeURIComponent(escape(atob(b64)));
                  return <pre className="p-4 bg-white rounded-lg text-sm whitespace-pre-wrap break-words max-h-[75vh] overflow-auto">{txt}</pre>;
                } catch { return <div className="p-6 text-sm">Could not render as text.</div>; }
              }
              return (
                <div className="p-8 text-center space-y-3">
                  <FileText size={48} className="mx-auto text-muted-foreground"/>
                  <div className="text-sm text-muted-foreground">This file type can't be previewed in the browser.</div>
                  <Button className="btn-pill bg-[hsl(149,40%,30%)]" onClick={() => download(preview.resource_id, preview.file_name)}>
                    <Download size={14} className="mr-2"/>Download to view
                  </Button>
                </div>
              );
            })()}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
